// Notifications — TanStack Query hooks. Бейдж колокольчика через refetchInterval
// (паттерн мессенджера); реалтайм не строим — поллинга достаточно.
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { useTenantId } from '@/lib/tenant-context';

import {
  getNotificationsSummary,
  listAuditLog,
  listClientAudit,
  listEnrollmentLog,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type AuditLogRow,
  type ClientAuditRow,
  type EmployeeNotification,
  type EnrollmentLogRow,
  type NotificationsSummary,
} from '../api/notifications.api';

const SUMMARY_POLL_MS = 30_000;

export function notificationsKeys(tenantId: string | null) {
  const root = ['notifications', tenantId] as const;
  return {
    root,
    summary: () => [...root, 'summary'] as const,
    list: (params: Record<string, unknown>) => [...root, 'list', params] as const,
    audit: (params: Record<string, unknown>) => [...root, 'audit', params] as const,
    clientAudit: (params: Record<string, unknown>) => [...root, 'client-audit', params] as const,
    enrollmentLog: (params: Record<string, unknown>) =>
      [...root, 'enrollment-log', params] as const,
  };
}

export function useNotificationsSummary(): UseQueryResult<NotificationsSummary> {
  const tenantId = useTenantId();
  const keys = notificationsKeys(tenantId);
  return useQuery({
    queryKey: keys.summary(),
    queryFn: getNotificationsSummary,
    refetchInterval: SUMMARY_POLL_MS,
  });
}

export function useNotificationsList(params: {
  limit?: number;
  unread_only?: boolean;
  kind_prefix?: string;
}): UseQueryResult<EmployeeNotification[]> {
  const tenantId = useTenantId();
  const keys = notificationsKeys(tenantId);
  return useQuery({
    queryKey: keys.list(params),
    queryFn: async () => (await listNotifications(params)).data,
    refetchInterval: SUMMARY_POLL_MS,
    // Вернулись во вкладку → сразу свежий список (тосты о новом появятся быстрее).
    refetchOnWindowFocus: true,
  });
}

export function useAuditLog(params: {
  from?: string;
  to?: string;
  employee_id?: string;
  entity_type?: string;
  q?: string;
  limit?: number;
}): UseQueryResult<AuditLogRow[]> {
  const tenantId = useTenantId();
  const keys = notificationsKeys(tenantId);
  return useQuery({
    queryKey: keys.audit(params),
    queryFn: async () => (await listAuditLog(params)).data,
  });
}

export function useClientAudit(params: {
  from?: string;
  to?: string;
  type?: 'new' | 'update';
  source?: 'employee' | 'system' | 'webhook';
  q?: string;
  limit?: number;
}): UseQueryResult<ClientAuditRow[]> {
  const tenantId = useTenantId();
  const keys = notificationsKeys(tenantId);
  return useQuery({
    queryKey: keys.clientAudit(params),
    queryFn: async () => (await listClientAudit(params)).data,
  });
}

export function useEnrollmentLog(params: {
  from?: string;
  to?: string;
  kind?: string;
  client_q?: string;
  limit?: number;
}): UseQueryResult<EnrollmentLogRow[]> {
  const tenantId = useTenantId();
  const keys = notificationsKeys(tenantId);
  return useQuery({
    queryKey: keys.enrollmentLog(params),
    queryFn: async () => (await listEnrollmentLog(params)).data,
  });
}

export function useMarkNotificationRead() {
  const tenantId = useTenantId();
  const keys = notificationsKeys(tenantId);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.root });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const tenantId = useTenantId();
  const keys = notificationsKeys(tenantId);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => markAllNotificationsRead(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.root });
    },
  });
}
